import { logger, LogContext } from './logger'

// per streamer layout and theme, read from the overlay url so each streamer can arrange it around their own scene
// e.g. ?userId=...&bets=left&qr=right&badge=left&theme=dark

export type Side = 'left' | 'right'
// off removes the element entirely, for streamers who only want part of the overlay
export type Placement = Side | 'off'
export type Theme = 'light' | 'dark' | 'auto'
// how wins are celebrated. separate from placements: fullscreen takes over the screen, muted opens a small panel
// on the edge opposite the bet card
export type WinAnimation = 'fullscreen' | 'muted' | 'off'
// whether the win celebration plays plc-app's notification sound
export type WinSound = 'on' | 'muted'

export type OverlaySettings = {
  theme: Theme
  winAnimation: WinAnimation
  winSound: WinSound
  positions: {
    liveBadge: Placement
    betList: Placement
    referralQr: Placement
  }
}

export const PLACEMENTS: Placement[] = ['left', 'right', 'off']
export const THEMES: Theme[] = ['auto', 'light', 'dark']
export const WIN_ANIMATIONS: WinAnimation[] = ['fullscreen', 'muted', 'off']
export const WIN_SOUNDS: WinSound[] = ['on', 'muted']

// query parameter names, shared with the url builder so the two can't drift apart
export const SETTING_PARAMS = {
  userId: 'userId',
  previewUserId: 'previewUserId',
  theme: 'theme',
  winAnimation: 'win',
  winSound: 'sound',
  liveBadge: 'badge',
  betList: 'bets',
  referralQr: 'qr',
  preview: 'preview',
  previewStakers: 'stakers',
} as const

export const DEFAULT_SETTINGS: OverlaySettings = {
  theme: 'auto',
  winAnimation: 'fullscreen',
  winSound: 'on',
  positions: {
    liveBadge: 'right',
    betList: 'right',
    referralQr: 'left',
  },
}

const readOption = <T extends string>(params: URLSearchParams, name: string, options: T[], fallback: T): T => {
  const raw = params.get(name)
  if (raw === null) return fallback
  const value = raw.trim().toLowerCase() as T
  if (options.includes(value)) return value
  // a typo shouldn't break the overlay, fall back and leave a trail for whoever is debugging the url
  logger.warn(LogContext.OVERLAY, 'Invalid overlay setting, using default', { setting: name, value: raw, default: fallback })
  return fallback
}

const readSettings = (): OverlaySettings => {
  const params = new URLSearchParams(window.location.search)
  const { positions } = DEFAULT_SETTINGS
  return {
    theme: readOption(params, SETTING_PARAMS.theme, THEMES, DEFAULT_SETTINGS.theme),
    winAnimation: readOption(params, SETTING_PARAMS.winAnimation, WIN_ANIMATIONS, DEFAULT_SETTINGS.winAnimation),
    winSound: readOption(params, SETTING_PARAMS.winSound, WIN_SOUNDS, DEFAULT_SETTINGS.winSound),
    positions: {
      liveBadge: readOption(params, SETTING_PARAMS.liveBadge, PLACEMENTS, positions.liveBadge),
      betList: readOption(params, SETTING_PARAMS.betList, PLACEMENTS, positions.betList),
      referralQr: readOption(params, SETTING_PARAMS.referralQr, PLACEMENTS, positions.referralQr),
    },
  }
}

// true when the url carries any layout or theme setting, so the link builder knows to start from them
export const hasSettingsInUrl = (): boolean => {
  const params = new URLSearchParams(window.location.search)
  const settingParams: string[] = [
    SETTING_PARAMS.theme,
    SETTING_PARAMS.winAnimation,
    SETTING_PARAMS.winSound,
    SETTING_PARAMS.liveBadge,
    SETTING_PARAMS.betList,
    SETTING_PARAMS.referralQr,
  ]
  return settingParams.some((name) => params.has(name))
}

// the muted win panel goes on the edge the bet card isn't using, left when the bet card is off (its default is right)
export const getMutedWinSide = (betList: Placement): Side => betList === 'left' ? 'right' : 'left'

// a setup link for a streamer: opens the link builder with their user id (and optionally a layout) filled in
export const buildSetupUrl = (baseUrl: string, userId: string, settings?: OverlaySettings): string => {
  const params = new URLSearchParams()
  params.set(SETTING_PARAMS.previewUserId, userId)
  if (settings) {
    params.set(SETTING_PARAMS.betList, settings.positions.betList)
    params.set(SETTING_PARAMS.referralQr, settings.positions.referralQr)
    params.set(SETTING_PARAMS.liveBadge, settings.positions.liveBadge)
    params.set(SETTING_PARAMS.winAnimation, settings.winAnimation)
    params.set(SETTING_PARAMS.winSound, settings.winSound)
    params.set(SETTING_PARAMS.theme, settings.theme)
  }
  return `${baseUrl}?${params.toString()}`
}

let cachedSettings: OverlaySettings | null = null

// the url doesn't change while the overlay runs, so read it once. lazily, so it happens after faro starts
// and any invalid setting warnings reach monitoring
export const getOverlaySettings = (): OverlaySettings => {
  if (!cachedSettings) cachedSettings = readSettings()
  return cachedSettings
}

// auto leaves design-system.css following the viewer's prefers-color-scheme, light and dark pin it (see app.css)
export const applyTheme = (theme: Theme = getOverlaySettings().theme) => {
  if (theme === 'auto') delete document.documentElement.dataset.theme
  else document.documentElement.dataset.theme = theme
}

// every setting is written out, even defaults, so a streamer reading their url can see what each part does
export const buildOverlayUrl = (
  baseUrl: string,
  userId: string,
  settings: OverlaySettings,
  { preview = false, previewStakers = false }: { preview?: boolean, previewStakers?: boolean } = {},
): string => {
  const params = new URLSearchParams()
  params.set(SETTING_PARAMS.userId, userId)
  params.set(SETTING_PARAMS.betList, settings.positions.betList)
  params.set(SETTING_PARAMS.referralQr, settings.positions.referralQr)
  params.set(SETTING_PARAMS.liveBadge, settings.positions.liveBadge)
  params.set(SETTING_PARAMS.winAnimation, settings.winAnimation)
  params.set(SETTING_PARAMS.winSound, settings.winSound)
  params.set(SETTING_PARAMS.theme, settings.theme)
  if (preview) params.set(SETTING_PARAMS.preview, '1')
  if (preview && previewStakers) params.set(SETTING_PARAMS.previewStakers, '1')
  return `${baseUrl}?${params.toString()}`
}

// the url builder embeds the overlay in an iframe, flag those so previews don't count as live streams
export const isPreviewMode = (): boolean =>
  new URLSearchParams(window.location.search).get(SETTING_PARAMS.preview) === '1'

// the builder's stakers preview, only honoured inside a preview so a pasted link can't fake stakers on stream
export const isStakersPreviewMode = (): boolean =>
  isPreviewMode() && new URLSearchParams(window.location.search).get(SETTING_PARAMS.previewStakers) === '1'
